import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImage, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Loader2, Star } from "lucide-react";

const catLabel = { Gold: "9K Gold", Silver: "Silver", GP: "Gold Plated" };

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = (products || []).filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl text-obsidian">Products</h1>
          <p className="text-sm text-muted-foreground">{products?.length || 0} chains in catalogue</p>
        </div>
        <button onClick={() => navigate("/admin/products/new")} className="inline-flex items-center gap-2 bg-obsidian text-white font-semibold px-5 py-2.5 hover:bg-gold hover:text-obsidian transition-colors rounded-sm" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Add New Chain
        </button>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm bg-white" data-testid="product-search" />
      </div>

      {products === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gold" /></div>
      ) : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4 font-semibold">Product</th>
                <th className="p-4 font-semibold hidden md:table-cell">Category</th>
                <th className="p-4 font-semibold">Price</th>
                <th className="p-4 font-semibold hidden sm:table-cell">Stock</th>
                <th className="p-4 font-semibold hidden lg:table-cell">Status</th>
                <th className="p-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-black/5 last:border-0 hover:bg-secondary/40" data-testid={`admin-product-${p.sku}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={resolveImage(p.images?.[0])} alt="" className="w-12 h-12 object-cover bg-secondary shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-obsidian truncate flex items-center gap-1.5">
                          {p.featured && <Star className="w-3.5 h-3.5 text-gold fill-gold" />}{p.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{p.sku}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-muted-foreground">{catLabel[p.category] || p.category}</td>
                  <td className="p-4">
                    <span className="font-semibold">{formatINR(p.price)}</span>
                    {p.discount_percent > 0 && <span className="block text-xs text-discount">{p.discount_percent}% off</span>}
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className={p.stock <= 0 ? "text-discount font-semibold" : ""}>{p.stock}</span>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <Badge className={`border-0 rounded-sm ${p.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{p.active ? "Active" : "Inactive"}</Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => navigate(`/admin/products/${p.id}/edit`)} className="p-2 hover:bg-secondary text-obsidian rounded-sm transition-colors" data-testid={`edit-${p.sku}`}><Pencil className="w-4 h-4" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-2 hover:bg-discount/10 text-discount rounded-sm transition-colors" data-testid={`delete-${p.sku}`}><Trash2 className="w-4 h-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone. The product will be permanently removed from your catalogue.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(p.id)} className="bg-discount hover:bg-discount/90" data-testid={`confirm-delete-${p.sku}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
